import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  AreaApi,
  checkCleanupNestedValidation,
  ILocationInfo,
  Log,
  RydeUiUnit,
} from "@ryde/shared";

export const useFindAreaButton = ({
  name,
  dropItemsSource,
  showFindAreaButtonFromSchema,
}: {
  name: string;
  dropItemsSource?: RydeUiUnit;
  showFindAreaButtonFromSchema?: boolean | null;
}) => {
  const showFindAreaButton =
    dropItemsSource === "area" && !!showFindAreaButtonFromSchema;
  const {
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const [isFindingArea, setIsFindingArea] = useState(false);

  const handleFindArea = useEvent(async (): Promise<void> => {
    if (!showFindAreaButton) {
      return;
    }

    const locationInfo = getValues("metadata.locationInfo") as
      ILocationInfo | undefined;
    const lat = locationInfo?.latitude;
    const lng = locationInfo?.longitude;

    if (lat === undefined || lng === undefined) {
      Log.warnAndToast(t("station.FIND_AREA_NO_COORDINATES"));
      return;
    }

    setIsFindingArea(true);
    try {
      const areas = await AreaApi.findByCoordinates({ lat, lng });
      if (!areas?.length) {
        Log.warnAndToast(t("station.FIND_AREA_NOT_FOUND"));
        return;
      }
      checkCleanupNestedValidation(name, errors);
      setValue(name, areas[0].id, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } catch (error) {
      Log.errorAndToast(t("station.FIND_AREA_ERROR"), error);
    } finally {
      setIsFindingArea(false);
    }
  });

  return { showFindAreaButton, isFindingArea, handleFindArea };
};
